import { TopBarTabsLayout } from './TopBarTabsLayout.js';
import { SidebarIconsLayout } from './SidebarIconsLayout.js';
import { HamburgerMenuLayout } from './HamburgerMenuLayout.js';
import { CategoryGridLayout } from './CategoryGridLayout.js';

export class LayoutManager {
  constructor(config, menuData, i18n = null) {
    this.config = config;
    this.menuData = menuData;
    this.currentLayout = null;
    this.cartCount = 0;
    this.i18n = i18n;
    
    this.initializeLayout();
  }

  /**
   * Initialize the appropriate layout based on configuration
   */
  initializeLayout() {
    const layoutType = this.getLayoutType();
    
    switch (layoutType) {
      case 'category-grid':
        this.currentLayout = new CategoryGridLayout(this.config, this.menuData, this.cartCount, this.i18n);
        break;
      case 'sidebar':
        this.currentLayout = new SidebarIconsLayout(this.config, this.menuData, this.cartCount, this.i18n);
        break;
      case 'hamburger':
        this.currentLayout = new HamburgerMenuLayout(this.config, this.menuData, this.cartCount, this.i18n);
        break;
      case 'tabs':
      default:
        this.currentLayout = new TopBarTabsLayout(this.config, this.menuData, this.cartCount, this.i18n);
        break;
    }
    
    // Ensure the layout is shown after initialization
    this.show();
  }

  /**
   * Get layout type from configuration
   * @returns {string} Layout type
   */
  getLayoutType() {
    const menuStyle = this.config?.kioskDisplaySettings?.layoutPreferences?.menuStyle;
    
    
    // Map API values to our internal layout types
    switch (menuStyle) {
      case 'grid':
        return 'category-grid';
      case 'sidebar':
        return 'sidebar';
      case 'hamburger':
        return 'hamburger';
      case 'tabs':
        return 'tabs';
      default:
        return 'tabs'; // Default fallback
    }
  }

  /**
   * Get layout preferences from configuration
   * @returns {Object} Layout preferences
   */
  getLayoutPreferences() {
    return this.config?.kioskDisplaySettings?.layoutPreferences || {
      menuPosition: 'left',
      columnsPerRow: 3,
      itemCardStyle: 'standard',
      showItemImages: true,
      expandedByDefault: true,
      showCategoryImages: true
    };
  }

  /**
   * Render the current layout
   * @returns {string} HTML string
   */
  render() {
    if (!this.currentLayout) {
      return '<div class="layout-error">Layout not initialized</div>';
    }
    
    const html = this.currentLayout.render();
    return html;
  }

  /**
   * Bind events for the current layout
   */
  bindEvents() {
    if (this.currentLayout && typeof this.currentLayout.bindEvents === 'function') {
      this.currentLayout.bindEvents();
    }
  }

  /**
   * Show the current layout
   */
  show() {
    if (this.currentLayout && typeof this.currentLayout.show === 'function') {
      this.currentLayout.show();
    }
  }

  /**
   * Hide the current layout
   */
  hide() {
    if (this.currentLayout && typeof this.currentLayout.hide === 'function') {
      return this.currentLayout.hide();
    }
  }

  /**
   * Update cart count across layouts
   * @param {number} count New cart count
   */
  updateCartCount(count) {
    this.cartCount = count;
    if (this.currentLayout && typeof this.currentLayout.updateCartCount === 'function') {
      this.currentLayout.updateCartCount(count);
    }
  }

  /**
   * Switch to a different layout (for dynamic switching)
   * @param {string} newLayoutType Layout type to switch to
   */
  switchLayout(newLayoutType) {
    if (this.currentLayout) {
      this.hide();
    }
    
    // Update config to reflect new layout
    if (!this.config.kioskDisplaySettings) {
      this.config.kioskDisplaySettings = {};
    }
    if (!this.config.kioskDisplaySettings.layoutPreferences) {
      this.config.kioskDisplaySettings.layoutPreferences = {};
    }
    
    this.config.kioskDisplaySettings.layoutPreferences.menuStyle = newLayoutType;
    
    // Reinitialize with new layout
    this.initializeLayout();
  }

  /**
   * Get current layout type
   * @returns {string} Current layout type
   */
  getCurrentLayoutType() {
    return this.getLayoutType();
  }

  /**
   * Check if layout supports specific features
   * @param {string} feature Feature to check
   * @returns {boolean} Whether feature is supported
   */
  supportsFeature(feature) {
    if (!this.currentLayout) return false;
    
    const supportedFeatures = this.currentLayout.getSupportedFeatures?.() || [];
    return supportedFeatures.includes(feature);
  }
}